/**
 * Recherche du planning : un numéro de bon (« 00016 »), une machine, un n° de série, un client ou un site. Les blocs de la
 * grille ne montrent qu'une semaine : un clic sur un résultat ouvre la semaine où l'intervention est prévue et la sélectionne
 * (demande de Benjamin, 2026-10-30 : « l'intervention n° … ne figure pas sur le planning, il faut les ouvrir une par une »).
 *
 * La liste vient de `GET /interventions?q=` (même recherche que la page des bons) ; une réponse tardive pour un texte déjà
 * changé est jetée. Aucune donnée financière.
 */
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { ApiError, api, type BonResume } from '../api/client';
import { ligneResultat, rechercheUtile } from '../domaine/planning-interventions';

const RESULTATS_AFFICHES = 8;

export function RecherchePlanning({ onChoisir }: { onChoisir: (b: BonResume) => void }) {
  const [texte, setTexte] = useState('');
  const [resultats, setResultats] = useState<{ q: string; items: readonly BonResume[]; total: number } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouverte, setOuverte] = useState(false);
  const [actif, setActif] = useState(0);
  const dernier = useRef('');

  useEffect(() => {
    const q = rechercheUtile(texte);
    dernier.current = q ?? '';
    if (q === null) {
      setResultats(null);
      setErreur(null);
      return;
    }
    const minuteur = window.setTimeout(() => {
      api
        .bons({ q, take: RESULTATS_AFFICHES })
        .then((l) => {
          if (dernier.current !== q) return;
          setResultats({ q, items: l.items, total: l.total });
          setErreur(null);
          setActif(0);
        })
        .catch((e: unknown) => {
          if (dernier.current !== q) return;
          setErreur(e instanceof ApiError ? e.message : 'Recherche impossible.');
        });
    }, 250);
    return () => window.clearTimeout(minuteur);
  }, [texte]);

  const choisir = (b: BonResume) => {
    onChoisir(b);
    setOuverte(false);
  };

  const surTouche = (e: KeyboardEvent<HTMLInputElement>) => {
    const items = resultats?.items ?? [];
    if (e.key === 'Escape') {
      e.preventDefault();
      setOuverte(false);
    } else if (e.key === 'ArrowDown' && items.length > 0) {
      e.preventDefault();
      setOuverte(true);
      setActif((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === 'ArrowUp' && items.length > 0) {
      e.preventDefault();
      setActif((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && items[actif]) {
      e.preventDefault();
      choisir(items[actif]);
    }
  };

  const montrer = ouverte && rechercheUtile(texte) !== null;

  return (
    <div className="recherche-planning">
      <input
        type="search"
        placeholder="N° de bon, machine, client…"
        aria-label="Rechercher une intervention"
        value={texte}
        onChange={(e) => {
          setTexte(e.target.value);
          setOuverte(true);
        }}
        onFocus={() => setOuverte(true)}
        onBlur={() => window.setTimeout(() => setOuverte(false), 150)}
        onKeyDown={surTouche}
      />
      {montrer && (
        <div className="recherche-planning-liste" role="listbox">
          {erreur && <div className="message erreur">{erreur}</div>}
          {!erreur && resultats === null && <div className="aide">Recherche…</div>}
          {!erreur && resultats !== null && resultats.items.length === 0 && (
            <div className="aide">Aucune intervention ne correspond à « {resultats.q} ».</div>
          )}
          {!erreur &&
            resultats !== null &&
            resultats.items.map((b, n) => {
              const l = ligneResultat(b);
              return (
                <div
                  key={b.id}
                  role="option"
                  aria-selected={n === actif}
                  className={`recherche-planning-ligne${n === actif ? ' actif' : ''}`}
                  onMouseEnter={() => setActif(n)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choisir(b);
                  }}
                >
                  <strong>{l.numero}</strong> <span>{l.machine}</span>
                  <div className="aide">
                    {l.client && `${l.client} — `}
                    {l.quand} · {l.etat}
                  </div>
                </div>
              );
            })}
          {!erreur && resultats !== null && resultats.total > resultats.items.length && (
            <div className="aide">
              {resultats.items.length} sur {resultats.total} : précisez la recherche.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
